import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const STARS = [1, 2, 3, 4, 5];

function ReferralShare({ t }) {
  const siteUrl = window.location.origin;
  const shareText = t('review.referralShareText', { url: siteUrl });

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: 'vQueue', text: shareText, url: siteUrl });
        return;
      } catch {
        // User cancelled or share API failed — fall through to clipboard.
      }
    }
    if (navigator.clipboard) {
      try { await navigator.clipboard.writeText(shareText); } catch { /* ignore */ }
    }
  };

  return (
    <div style={referralWrapStyle}>
      <div style={referralTitleStyle}>{t('review.referralTitle')}</div>
      <div style={referralBodyStyle}>{t('review.referralBody')}</div>
      <button type="button" onClick={handleShare} style={referralBtnStyle}>
        {t('review.referralCta')}
      </button>
    </div>
  );
}

/**
 * Defence-in-depth check: the backend already validates review URLs with
 * a @Pattern constraint ("^$|^https?://.+"), but if a stale bundle or a
 * misconfigured row ever slips through, we must not blindly follow
 * "javascript:" or other non-http schemes from a kiosk tablet that's
 * displayed to end customers.
 */
function isSafeReviewUrl(raw) {
  if (!raw || typeof raw !== 'string') return false;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:';
  } catch {
    return false;
  }
}

/**
 * Post-service review prompt.
 *
 * Shows a 1-5 star rating after the customer's ticket is completed.
 * - Ratings >= threshold (usually 4) redirect to the business's configured
 *   review URL (Google Review, Trustpilot, etc.) — a reputation-filter
 *   pattern where only happy customers are routed to public review sites.
 * - Ratings < threshold show a private thank-you — no public review, no
 *   data stored server-side (zero PII principle).
 * - A single "Skip" button always lets the customer opt out.
 *
 * The parent `QueuePage` is responsible for rendering this component only
 * when the shop has `reviewPromptEnabled && reviewUrl`. Guard included
 * here for defense in depth.
 */
export default function ReviewPromptScreen({ reviewUrl, threshold = 4, onDismiss }) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!rating) return;
    setSubmitted(true);
    if (rating >= threshold && isSafeReviewUrl(reviewUrl)) {
      // Delay briefly so the user sees their selection before redirect.
      setTimeout(() => {
        window.location.assign(reviewUrl);
      }, 400);
    }
  };

  // Thank-you screen — shown for low ratings, or after a high-rating
  // submission when no safe review URL is configured (defence in depth).
  if (submitted && (rating < threshold || !isSafeReviewUrl(reviewUrl))) {
    return (
      <div style={wrapperStyle}>
        <div style={{ fontSize: '4rem' }}>🙏</div>
        <h2 style={titleStyle}>{t('review.thanksTitle')}</h2>
        <p style={bodyStyle}>{t('review.thanksBody')}</p>
        <ReferralShare t={t} />
        <button type="button" onClick={onDismiss} style={dismissBtnStyle}>
          {t('review.close')}
        </button>
      </div>
    );
  }

  // In-flight redirect state
  if (submitted && rating >= threshold && isSafeReviewUrl(reviewUrl)) {
    return (
      <div style={wrapperStyle}>
        <div style={{ fontSize: '4rem' }}>⭐</div>
        <h2 style={titleStyle}>{t('review.redirectingTitle')}</h2>
        <p style={bodyStyle}>{t('review.redirectingBody')}</p>
        <ReferralShare t={t} />
      </div>
    );
  }

  return (
    <div style={wrapperStyle}>
      <h2 style={titleStyle}>{t('review.promptTitle')}</h2>
      <p style={bodyStyle}>{t('review.promptBody')}</p>

      <div
        role="radiogroup"
        aria-label={t('review.ratingAria')}
        style={starsWrapperStyle}
        onMouseLeave={() => setHover(0)}
      >
        {STARS.map((s) => {
          const active = (hover || rating) >= s;
          return (
            <button
              key={s}
              type="button"
              role="radio"
              aria-checked={rating === s}
              aria-label={t('review.starAria', { count: s })}
              onClick={() => setRating(s)}
              onMouseEnter={() => setHover(s)}
              onFocus={() => setHover(s)}
              onBlur={() => setHover(0)}
              style={{
                ...starButtonStyle,
                color: active ? '#F59E0B' : '#334155',
                transform: hover === s ? 'scale(1.15)' : 'scale(1)',
              }}
            >
              {active ? '★' : '☆'}
            </button>
          );
        })}
      </div>

      <div style={buttonRowStyle}>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!rating}
          style={{ ...submitBtnStyle, opacity: rating ? 1 : 0.5, cursor: rating ? 'pointer' : 'not-allowed' }}
        >
          {t('review.submit')}
        </button>
        <button type="button" onClick={onDismiss} style={skipBtnStyle}>
          {t('review.skip')}
        </button>
      </div>
    </div>
  );
}

const wrapperStyle = {
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 16,
  padding: '1rem 0',
};

const titleStyle = {
  margin: 0,
  fontSize: '1.4rem',
  fontWeight: 800,
  color: '#F8FAFC',
};

const bodyStyle = {
  margin: 0,
  fontSize: '0.95rem',
  color: '#94A3B8',
  lineHeight: 1.5,
  maxWidth: 360,
};

const starsWrapperStyle = {
  display: 'flex',
  gap: '0.5rem',
  margin: '0.75rem 0 0.5rem',
};

const starButtonStyle = {
  background: 'transparent',
  border: 'none',
  padding: 4,
  fontSize: '3rem',
  lineHeight: 1,
  cursor: 'pointer',
  transition: 'transform 0.15s ease, color 0.15s ease',
  WebkitTapHighlightColor: 'transparent',
};

const buttonRowStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: '100%',
  maxWidth: 280,
  marginTop: 12,
};

const submitBtnStyle = {
  padding: '14px 24px',
  background: '#7C3AED',
  color: '#fff',
  border: 'none',
  borderRadius: 12,
  fontSize: '1rem',
  fontWeight: 700,
};

const skipBtnStyle = {
  padding: '12px 24px',
  background: 'transparent',
  color: '#94A3B8',
  border: '1px solid #334155',
  borderRadius: 12,
  fontSize: '0.95rem',
  fontWeight: 600,
  cursor: 'pointer',
};

const dismissBtnStyle = {
  ...submitBtnStyle,
  marginTop: 8,
};

const referralWrapStyle = {
  marginTop: 12,
  padding: '14px 16px',
  border: '1px solid #334155',
  borderRadius: 12,
  background: 'rgba(124,58,237,0.08)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  maxWidth: 320,
  width: '100%',
};

const referralTitleStyle = {
  fontSize: '0.95rem',
  fontWeight: 700,
  color: '#F8FAFC',
};

const referralBodyStyle = {
  fontSize: '0.85rem',
  color: '#94A3B8',
  textAlign: 'center',
};

const referralBtnStyle = {
  marginTop: 6,
  padding: '8px 18px',
  borderRadius: 10,
  border: 'none',
  background: 'linear-gradient(135deg, #7C3AED, #06B6D4)',
  color: '#fff',
  fontWeight: 700,
  fontSize: '0.9rem',
  cursor: 'pointer',
};
